function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-4"
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[#6B5E53] text-base leading-[1.9] mb-0"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function EditorialImage({ src, caption }: { src: string; caption: string }) {
  return (
    <div className="my-14 md:my-20 -mx-4 md:-mx-8">
      <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-2xl bg-[#EDE6DA]">
        <img
          src={src}
          alt={caption}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p
        className="text-center text-[#B5A898] text-[11px] tracking-[0.15em] mt-4 uppercase"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {caption}
      </p>
    </div>
  );
}

export default function ArticleContentSecondHalf() {
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0">
      {/* Image 4 */}
      <EditorialImage
        src="https://public.readdy.ai/ai/img_res/fa809938f6f772f00db67f525e713c04.jpg"
        caption="Calm Layout — Space to Breathe, Space to Rest"
      />

      {/* Section 8 */}
      <div className="mb-14">
        <SectionHeading>Bring in light wood for warmth</SectionHeading>
        <SectionText>
          Wood is one of the most important materials in a soft beige interior. Light oak, ash, and pale walnut each bring a living warmth that synthetic materials cannot replicate — a warmth that changes with the light, deepens with age, and connects the interior to the natural world outside. Use wood in a coffee table, a side table, a shelf, a lamp base, or a floor. Even a single wooden element grounds a beige room and gives it something real to hold onto.
        </SectionText>
        <div className="mt-6">
          <SectionText>
            Avoid dark or heavily stained wood in a soft beige room — it creates too much contrast and pulls the eye away from the calm. Stick to light, natural finishes that sit comfortably within the warm neutral palette.
          </SectionText>
        </div>
      </div>

      {/* Section 9 */}
      <div className="mb-14">
        <SectionHeading>Layer warm whites and creams</SectionHeading>
        <SectionText>
          One of the most beautiful aspects of a neutral interior is the range within it. Cream, ivory, sand, oatmeal, taupe, and warm white are not interchangeable — they each carry a slightly different temperature and weight. Layering these tones creates a rich, dimensional interior that shifts in different lights. A cream rug, ivory cushions, an oatmeal throw, warm white walls, and a taupe armchair — together, these create a room that feels complex and alive despite using no saturated colour at all.
        </SectionText>
      </div>

      {/* Section 10 */}
      <div className="mb-14">
        <SectionHeading>Create one quiet focal point</SectionHeading>
        <SectionText>
          Every room benefits from having somewhere for the eye to settle. In a calm beige living room, this focal point should be quiet rather than dramatic. A sunlit sofa corner with cushions arranged softly. A simple coffee table styled with a ceramic piece, a candle, and a single stem. A reading chair positioned beside a warm lamp with a small stack of books beside it. A large window framed by linen curtains with the morning light coming through. Any of these can be the emotional heart of the room — the place that makes it feel considered and deeply comfortable.
        </SectionText>
      </div>

      {/* Image 5 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=quiet%20reading%20corner%20beige%20tones%20linen%20armchair%20warm%20lamp%20book%20stack%20small%20side%20table%20cream%20neutral%20interior%20soft%20light%20peaceful%20cozy%20intimate%20editorial%20photography%20calm%20atmosphere%20beautiful&width=1400&height=600&seq=sb_img5&orientation=landscape"
        caption="A Quiet Focal Point — The Emotional Heart of the Room"
      />
    </div>
  );
}
